<template>
	<div class="OrderItemEditForm" data-cy="editForm">
		<div class="form-grid">
			<ContingentDateField
				:model-value="row.draft.date"
				:time="row.draft.time"
				:days="days"
				:min-date="minDate"
				:max-date="row.item.availabilityUntil"
				:loading="row.contingentsLoading"
				@update:model-value="onDateChange"
				@update:month="$emit('update:month', $event)"
			/>
			<TimeslotSelect
				:model-value="row.draft.time"
				:slots="selectedDaySlots"
				:disabled="!hasTimeslots"
				@update:model-value="patch({ time: $event })"
			/>
			<div>
				<v-label class="field-label">{{ $t('text.ticketType') }}</v-label>
				<v-text-field :model-value="row.item.ticketType" disabled variant="outlined" density="compact" hide-details data-cy="ticketType" />
			</div>
			<div>
				<v-label class="field-label">{{ $t('text.ticketCategory') }}</v-label>
				<v-text-field :model-value="row.item.ticketCategory" disabled variant="outlined" density="compact" hide-details data-cy="ticketCategory" />
			</div>
			<div v-if="showNameFields">
				<v-label class="field-label">{{ $t('text.firstName') }}</v-label>
				<v-text-field :model-value="row.draft.firstName" variant="outlined" density="compact" hide-details
					data-cy="firstName" @update:model-value="patch({ firstName: $event })" />
			</div>
			<div v-if="showNameFields">
				<v-label class="field-label">{{ $t('text.lastName') }}</v-label>
				<v-text-field :model-value="row.draft.lastName" variant="outlined" density="compact" hide-details
					data-cy="lastName" @update:model-value="patch({ lastName: $event })" />
			</div>
			<div v-if="showEmailField" class="full-width">
				<v-label class="field-label">{{ $t('text.email') }} *</v-label>
				<v-text-field :model-value="row.draft.email" variant="outlined" density="compact"
					:hide-details="!emailError" :error-messages="emailError"
					data-cy="email" @update:model-value="patch({ email: $event })" />
			</div>
			<div class="full-width">
				<PersonalizationFields
					:model-value="row.draft.additional"
					:fields="visibleAdditionalFields"
					@update:model-value="patch({ additional: $event })"
				/>
			</div>
		</div>
		<div v-if="row.priceDiff?.warning" class="price-hint" data-cy="priceHint">
			<v-icon size="16" color="#ffb400">mdi-alert</v-icon>
			{{ $t('text.priceChangeDetected') }}:
			<s>{{ formatPrice(row.priceDiff.oldPrice, row.item.currency) }}</s>
			<b>{{ formatPrice(row.priceDiff.newPrice, row.item.currency) }}</b>
		</div>
	</div>
</template>

<script>
import ContingentDateField from './ContingentDateField.vue'
import PersonalizationFields from './PersonalizationFields.vue'
import TimeslotSelect from './TimeslotSelect.vue'
import { formatPrice, isValidEmail } from './rebooking-utils'

export default {
	name: 'OrderItemEditForm',
	components: { ContingentDateField, PersonalizationFields, TimeslotSelect },
	props: {
		row: { type: Object, required: true },
		minDate: String,
	},
	computed: {
		days() {
			// date -> slots[] map for the calendar
			const map = {}
			for (const day of this.row.contingents ?? []) map[day.date] = day.slots
			return map
		},
		selectedDaySlots() {
			return this.row.draft.date ? (this.days[this.row.draft.date] ?? []) : []
		},
		hasTimeslots() {
			const source = this.selectedDaySlots.length ? this.selectedDaySlots : Object.values(this.days).flat()
			return source.some(slot => slot.time !== '00:00')
		},
		emailError() {
			return isValidEmail(this.row.draft.email) ? '' : this.$t('text.invalidEmail')
		},
		// only render the attributes the PEAK person actually has (from the order read) — PEAK
		// rejects editing/adding any attribute the person does not already have.
		showNameFields() {
			const p = this.row.item.personalization ?? {}
			return p.firstName !== undefined || p.lastName !== undefined
		},
		showEmailField() {
			return (this.row.item.personalization ?? {}).email !== undefined
		},
		visibleAdditionalFields() {
			const present = this.row.item.personalization?.additional ?? {}
			return (this.row.item.personalizationConfig?.additional ?? []).filter(field => field.id in present)
		},
	},
	methods: {
		formatPrice,
		patch(fields) {
			this.$emit('update:draft', fields)
		},
		onDateChange(date) {
			// reset the timeslot when it does not exist on the newly picked date
			const slots = (this.days[date] ?? []).map(slot => slot.time)
			const keepTime = this.row.draft.time && slots.includes(this.row.draft.time)
			this.patch({ date, time: keepTime ? this.row.draft.time : null })
		},
	},
}
</script>

<style scoped>
.OrderItemEditForm {
	padding: 16px 24px 24px 24px;
	background: #fafafa;
}
.form-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px 24px;
}
.full-width { grid-column: 1 / -1; }
.field-label { font-size: 13px; margin-bottom: 4px; display: block; }
.price-hint {
	margin-top: 12px;
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	color: #664800;
}
@media (max-width: 767px) {
	.form-grid { grid-template-columns: 1fr; }
}
</style>
